import {
    CreditCardState,
    PaymentCardFormat,
    PaymentMethodType,
    PayoutTransactionDetailResult,
    SepaDirectDebitProviderState,
} from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import { Stack, Tag, Typography } from '@optimus-web/core';
import { LocalizedEnum } from '@mews/framework';
import { PaymentMethodIcon } from '../../../../components/commander-ui/PaymentMethodIcon';
import PaymentCardIcon from '../../../../components/commander-ui/PaymentCardIcon';
import { PaymentDetailsSidePanelRow } from './PaymentDetailsSidePanelRow';
import { creditCardStateTagVariantMap, paymentMethodImageTypeMap, sepaStateTagVariantMap } from './utils';

const paymentMethodLastDigitsRow = (type: PaymentMethodType, lastDigits: string) => (
    <PaymentDetailsSidePanelRow labelTextKey={TextKey.Method}>
        <Stack itemAlignment="center" spacing="50">
            <PaymentMethodIcon type={paymentMethodImageTypeMap[type]} small />
            <Typography textStyle="bodyMedium" as="div">
                {lastDigits}
            </Typography>
        </Stack>
    </PaymentDetailsSidePanelRow>
);

const creditCardStatusRow = (status: CreditCardState) => (
    <PaymentDetailsSidePanelRow labelTextKey={TextKey.Status} hideIfNull>
        <Tag variant={creditCardStateTagVariantMap[status]}>
            <LocalizedEnum prefix="CreditCardState" value={status} />
        </Tag>
    </PaymentDetailsSidePanelRow>
);

const sepaStatusRow = (status: SepaDirectDebitProviderState) => (
    <PaymentDetailsSidePanelRow labelTextKey={TextKey.Status} hideIfNull>
        <Tag variant={sepaStateTagVariantMap[status]}>
            <LocalizedEnum prefix="SepaDirectDebitProviderState" value={status} />
        </Tag>
    </PaymentDetailsSidePanelRow>
);

const formatRow = (format: PaymentCardFormat) => (
    <PaymentDetailsSidePanelRow labelTextKey={TextKey.Type} hideIfNull>
        <Typography textStyle="bodyMedium" as="div">
            <LocalizedEnum prefix="PaymentCardFormat" value={format} />
        </Typography>
    </PaymentDetailsSidePanelRow>
);

export const PaymentDetailSidePanelPaymentMethod = ({
    paymentMethod,
}: {
    paymentMethod: NonNullable<PayoutTransactionDetailResult['paymentMethod']>;
}) => {
    if (paymentMethod.discriminator === PaymentMethodType.Ideal) {
        return (
            <>
                <PaymentDetailsSidePanelRow labelTextKey={TextKey.Method}>
                    <Stack itemAlignment="center" spacing="50">
                        <PaymentMethodIcon type={paymentMethodImageTypeMap[paymentMethod.discriminator]} small />
                        <Typography textStyle="bodyMedium" as="div">
                            iDEAL
                        </Typography>
                    </Stack>
                </PaymentDetailsSidePanelRow>
            </>
        );
    }

    if (paymentMethod.discriminator === PaymentMethodType.PaymentCard) {
        return (
            <>
                <PaymentDetailsSidePanelRow labelTextKey={TextKey.Method}>
                    <Stack itemAlignment="center" spacing="50">
                        <PaymentCardIcon type={paymentMethod.value.type} width={24} />
                        <Typography textStyle="bodyMedium" as="div">
                            {paymentMethod.value.lastDigits ?? ''}
                        </Typography>
                    </Stack>
                </PaymentDetailsSidePanelRow>
                <PaymentDetailsSidePanelRow labelTextKey={TextKey.Owner}>
                    {paymentMethod.value.holderName && (
                        <Typography textStyle="bodyMedium" as="div">
                            {paymentMethod.value.holderName}
                        </Typography>
                    )}
                </PaymentDetailsSidePanelRow>
                {creditCardStatusRow(paymentMethod.value.status)}
                {formatRow(paymentMethod.value.format)}
            </>
        );
    }

    if (
        paymentMethod.discriminator === PaymentMethodType.ApplePay ||
        paymentMethod.discriminator === PaymentMethodType.GooglePay
    ) {
        return (
            <>
                {paymentMethodLastDigitsRow(paymentMethod.discriminator, paymentMethod.value.lastDigits)}
                {creditCardStatusRow(paymentMethod.value.status)}
                {formatRow(paymentMethod.value.format)}
            </>
        );
    }

    if (paymentMethod.discriminator === PaymentMethodType.SepaDirectDebit) {
        return (
            <>
                {paymentMethodLastDigitsRow(paymentMethod.discriminator, paymentMethod.value.lastDigits)}
                {sepaStatusRow(paymentMethod.value.status)}
                <PaymentDetailsSidePanelRow labelTextKey={TextKey.PayoutDetailMandateReference}>
                    {paymentMethod.value.mandateReference && (
                        <Typography textStyle="bodyMedium" as="div">
                            {paymentMethod.value.mandateReference}
                        </Typography>
                    )}
                </PaymentDetailsSidePanelRow>
            </>
        );
    }

    return null;
};
